import { useMutation, useQuery } from '@tanstack/react-query';

import { SdpResponse } from '../models/SdpResponse';
import { SdpUpdateRequest } from '../models/SdpUpdateRequest';
import axiosClient from '../axiosBearerClient';

export const useGetSdp = (sdpId?: string) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['sdp', sdpId],
    enabled: !!sdpId,
    retry: false,
    queryFn: async () => {
      return await axiosClient.get<SdpResponse>(`/sdps/${sdpId}`);
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetOverview = () => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['overview'],
    gcTime: 0,
    retry: false,
    queryFn: async () => {
      return await axiosClient.get<SdpResponse[]>(`/sdps/overview`);
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useGetIsAccMember = (ean?: string | null | undefined) => {
  const { isFetching, error, data, isError, refetch } = useQuery({
    queryKey: ['acc-member', ean],
    enabled: !!ean,
    retry: false,
    queryFn: async () => {
      return await axiosClient.get<SdpResponse[]>(`/sdps/${ean}/accMember`);
    },
  });

  return {
    isFetching,
    error,
    isError,
    data,
    refetch,
  };
};

export const useExportSdpData = () => {
  const { isPending, error, data, isError, mutateAsync } = useMutation({
    gcTime: 0,
    retry: false,
    mutationFn: async (sdpId?: string) => {
      return await axiosClient.post(`/sdps/${sdpId}/export`);
    },
  });

  return {
    isPending,
    error,
    isError,
    data,
    mutateAsync,
  };
};

export const useDeleteSdps = () => {
  const { isPending, error, data, isError, mutateAsync } = useMutation({
    retry: false,
    mutationFn: async (ids: string[]) => {
      const promises = ids.map((id) => axiosClient.delete(`/sdps/${id}`));
      return await Promise.all(promises);
    },
  });

  return {
    isPending,
    error,
    isError,
    data,
    mutateAsync,
  };
};

export const useUpdateSdp = () => {
  const { isPending, error, data, isError, mutateAsync } = useMutation({
    retry: false,
    mutationFn: async (sdp: SdpUpdateRequest) => {
      return axiosClient.put(`/sdps/${sdp.id}`, sdp);
    },
  });

  return {
    isPending,
    error,
    isError,
    data,
    mutateAsync,
  };
};
